import { useCallback, useEffect, useRef, useState } from "react";
import { Col, Modal, Spinner } from "react-bootstrap";
import { IoCloseSharp, IoChevronDown, IoChevronUp } from "react-icons/io5";
import { BsSearch } from "react-icons/bs";
import { useLazyQuery } from "@apollo/client";
import {
  netural400,
  neturalBlack,
  primaryGrey,
  primaryOrange,
} from "../../constants/color";
import CustomButton from "../button/button.component";
import { FETCH_PRODUCTS_FOR_SLICE } from "../../services/graphql/admin/rewardWheel";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const ProductSelectionModal = ({ show, onHide, onProductSelected }) => {
  const [searchWord, setSearchWord] = useState("");
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [page, setPage] = useState(1);
  const debounceTimer = useRef(null);

  /* ─── Query ─── */
  const [fetchProducts, { data, loading }] = useLazyQuery(
    FETCH_PRODUCTS_FOR_SLICE,
    { fetchPolicy: "network-only" },
  );

  const result = data?.fetchProductsForSlice;
  const products = result?.products ?? [];
  const totalCount = result?.totalCount ?? 0;
  const hasNext = result?.hasNext ?? false;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  /* ─── Fetch when modal opens or page changes ─── */
  useEffect(() => {
    if (show) {
      fetchProducts({
        variables: {
          searchKeyword: searchWord || null,
          pageNumber: page,
          pageSize: PAGE_SIZE,
        },
      });
    }
  }, [show, page]);

  /* ─── Debounced search ─── */
  const debouncedSearch = useCallback(
    (keyword) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        setPage(1);
        fetchProducts({
          variables: {
            searchKeyword: keyword || null,
            pageNumber: 1,
            pageSize: PAGE_SIZE,
          },
        });
      }, SEARCH_DEBOUNCE_MS);
    },
    [fetchProducts],
  );

  /* ─── Helpers for variant / non-variant products ─── */
  const isVariantProduct = (product) => product.variantCombinationsCount > 0;

  const getProductImage = (product) =>
    product.medias?.[0]?.uri_thumbnail || product.medias?.[0]?.uri || "/images/icon.png";

  const getDisplayItems = (product) => {
    if (isVariantProduct(product)) {
      return product.variantCombinations.map((combo) => ({
        id: combo.sku || combo.combination,
        name: combo.combination,
        price: parseFloat(combo.finalTotal || combo.price || "0"),
        stock: parseInt(combo.quantity || "0", 10),
        isCombo: true,
      }));
    }
    // Non-variant product — single item
    return [
      {
        id: null,
        name: product.title,
        price: parseFloat(product.finalTotal || product.price || "0"),
        stock: parseInt(product.quantity || "0", 10),
        isCombo: false,
      },
    ];
  };

  /* ─── Handlers ─── */
  const toggleExpand = (productId) => {
    setExpandedProduct((prev) => (prev === productId ? null : productId));
  };

  const handleSelectVariant = (product, item) => {
    setSelectedVariant({
      productId: product._id,
      variantId: item.isCombo ? item.id : null,
      productName: product.title,
      variantName: item.isCombo ? item.name : null,
      businessId: product.businessId,
      price: item.price,
    });
  };

  const handleAddProduct = () => {
    if (selectedVariant) {
      onProductSelected(selectedVariant);
    }
    handleClose();
  };

  const handleClose = () => {
    setSearchWord("");
    setExpandedProduct(null);
    setSelectedVariant(null);
    setPage(1);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    onHide();
  };

  const handleSearchChange = (value) => {
    setSearchWord(value);
    debouncedSearch(value);
  };

  /* ─── Pagination numbers ─── */
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      backdrop="static"
      aria-labelledby="product-selection-modal"
    >
      {/* ─── Header ─── */}
      <Modal.Header
        style={{
          borderBottom: "none",
          paddingLeft: "24px",
          paddingRight: "24px",
          paddingTop: "20px",
          paddingBottom: "12px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <label
          style={{
            fontSize: "18px",
            fontWeight: "bold",
            color: neturalBlack,
          }}
        >
          Add products
        </label>
        <IoCloseSharp
          size={24}
          color={netural400}
          onClick={handleClose}
          style={{ cursor: "pointer" }}
        />
      </Modal.Header>

      <Modal.Body style={{ padding: 0 }}>
        {/* ─── Search bar ─── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "12px 24px",
            gap: "12px",
            borderTop: `1px solid ${primaryGrey}`,
            borderBottom: `1px solid ${primaryGrey}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              border: `1px solid ${primaryGrey}`,
              borderRadius: "10px",
              padding: "8px 12px",
              backgroundColor: "#fff",
            }}
          >
            <BsSearch
              color={netural400}
              size={16}
              style={{ marginRight: "8px", flexShrink: 0 }}
            />
            <input
              type="text"
              placeholder="Search products"
              value={searchWord}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{
                border: "none",
                outline: "none",
                flex: 1,
                fontSize: "14px",
                color: neturalBlack,
                backgroundColor: "transparent",
              }}
            />
          </div>
          <label
            style={{
              fontSize: "14px",
              color: primaryOrange,
              fontWeight: "600",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
            onClick={() => handleSearchChange("")}
          >
            Clear
          </label>
        </div>

        {/* ─── Product list ─── */}
        <div style={{ maxHeight: "50vh", overflowY: "auto", padding: "0 24px" }}>
          {loading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "60px 0",
              }}
            >
              <Spinner animation="border" size="sm" style={{ color: primaryOrange, marginRight: "8px" }} />
              <label style={{ fontSize: "14px", color: netural400 }}>Loading products...</label>
            </div>
          ) : products.length === 0 ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "60px 0",
              }}
            >
              <label style={{ fontSize: "16px", fontWeight: "500", color: netural400 }}>
                No products found. Try different keywords.
              </label>
            </div>
          ) : (
            products.map((product) => {
              const displayItems = getDisplayItems(product);
              const hasVariants = isVariantProduct(product);
              const itemCount = hasVariants
                ? product.variantCombinationsCount
                : 1;
              const productImage = getProductImage(product);

              return (
                <div key={product._id}>
                  {/* Product row */}
                  <div
                    onClick={() => toggleExpand(product._id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "14px 0",
                      borderBottom: `1px solid ${primaryGrey}`,
                      cursor: "pointer",
                      gap: "12px",
                    }}
                  >
                    {/* Product image */}
                    <img
                      src={`${process.env.REACT_APP_HOST_URL}${productImage}`}
                      alt={product.title}
                      style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "8px",
                        objectFit: "cover",
                        backgroundColor: "#f5f5f5",
                        border: `1px solid ${primaryGrey}`,
                      }}
                    />

                    {/* Product info */}
                    <div style={{ flex: 1 }}>
                      <label
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: neturalBlack,
                          display: "block",
                          cursor: "pointer",
                        }}
                      >
                        {product.title}
                      </label>
                      <label
                        style={{
                          fontSize: "12px",
                          color: netural400,
                          cursor: "pointer",
                        }}
                      >
                        {hasVariants
                          ? `${itemCount} variant${itemCount !== 1 ? "s" : ""}`
                          : "No variants"}
                        {product.companyName && ` · ${product.companyName}`}
                      </label>
                    </div>

                    {/* Expand chevron */}
                    {expandedProduct === product._id ? (
                      <IoChevronUp size={18} color={netural400} />
                    ) : (
                      <IoChevronDown size={18} color={netural400} />
                    )}
                  </div>

                  {/* Expanded items (variants or single product) */}
                  {expandedProduct === product._id && (
                    <div style={{ backgroundColor: "#FAFAFA" }}>
                      {displayItems.map((item) => {
                        const isInStock = item.stock > 0;
                        const isSelected =
                          selectedVariant?.productId === product._id &&
                          selectedVariant?.variantId === item.id;

                        return (
                          <div
                            key={item.id ?? product._id}
                            onClick={() => {
                              if (isInStock) handleSelectVariant(product, item);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "12px 16px 12px 56px",
                              borderBottom: `1px solid ${primaryGrey}`,
                              cursor: isInStock ? "pointer" : "not-allowed",
                              gap: "12px",
                              backgroundColor: !isInStock
                                ? "#F2F2F2"
                                : isSelected
                                ? "#FFF7F0"
                                : "transparent",
                              opacity: isInStock ? 1 : 0.6,
                            }}
                          >
                            {/* Item name */}
                            <label
                              style={{
                                flex: 1,
                                fontSize: "14px",
                                color: isInStock ? neturalBlack : netural400,
                                cursor: isInStock ? "pointer" : "not-allowed",
                              }}
                            >
                              {item.name}
                            </label>

                            {/* Price */}
                            <label
                              style={{
                                fontSize: "14px",
                                color: isInStock ? neturalBlack : netural400,
                                fontWeight: "500",
                                minWidth: "70px",
                                textAlign: "right",
                                cursor: isInStock ? "pointer" : "not-allowed",
                              }}
                            >
                              ${item.price.toFixed(2)}
                            </label>

                            {/* Stock badge */}
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "2px 10px",
                                borderRadius: "20px",
                                fontSize: "12px",
                                fontWeight: "600",
                                backgroundColor: isInStock ? "#E6F6E6" : "#FDE7E7",
                              color: isInStock ? "#037701" : "#EA1010",
                              minWidth: "80px",
                              justifyContent: "center",
                            }}
                          >
                            {isInStock ? "In Stock" : "Out of Stock"}
                          </div>

                          {/* Available count */}
                          <label
                            style={{
                              fontSize: "13px",
                              color: netural400,
                              minWidth: "30px",
                              textAlign: "right",
                              cursor: isInStock ? "pointer" : "not-allowed",
                            }}
                          >
                            {item.stock}
                          </label>

                          {/* Radio */}
                          <input
                            type="radio"
                            name="productVariantSelection"
                            checked={isSelected}
                            disabled={!isInStock}
                            onChange={() =>
                              handleSelectVariant(product, item)
                            }
                            style={{
                              width: "18px",
                              height: "18px",
                              accentColor: primaryOrange,
                              cursor: "pointer",
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              );
            })
          )}
        </div>

        {/* ─── Pagination ─── */}
        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "4px",
              padding: "12px 24px 4px",
            }}
          >
            <span
              onClick={() => page > 1 && setPage(page - 1)}
              style={{
                padding: "4px 10px",
                cursor: page > 1 ? "pointer" : "default",
                color: page > 1 ? neturalBlack : primaryGrey,
                fontSize: "16px",
                fontWeight: "600",
                userSelect: "none",
              }}
            >
              &lsaquo;
            </span>
            {getPageNumbers().map((num) => (
              <span
                key={num}
                onClick={() => setPage(num)}
                style={{
                  padding: "4px 10px",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: num === page ? "700" : "400",
                  color: num === page ? "#fff" : neturalBlack,
                  backgroundColor: num === page ? primaryOrange : "transparent",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                {num}
              </span>
            ))}
            <span
              onClick={() => hasNext && setPage(page + 1)}
              style={{
                padding: "4px 10px",
                cursor: hasNext ? "pointer" : "default",
                color: hasNext ? neturalBlack : primaryGrey,
                fontSize: "16px",
                fontWeight: "600",
                userSelect: "none",
              }}
            >
              &rsaquo;
            </span>
          </div>
        )}

        {/* ─── Divider ─── */}
        <div
          style={{
            borderTop: `1px solid ${primaryGrey}`,
            margin: "8px 24px 0",
          }}
        />

        {/* ─── Footer ─── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "16px 24px 20px",
          }}
        >
          <Col lg="7" md="7" sm="7" xs="7">
            <label
              style={{
                fontSize: "14px",
                fontWeight: "500",
                color: neturalBlack,
              }}
            >
              {selectedVariant ? "1 product selected" : "0 product selected"}
            </label>
          </Col>
          <Col
            lg="2"
            md="2"
            sm="2"
            xs="2"
            style={{ marginRight: "8px" }}
          >
            <CustomButton
              buttonType="secondary"
              title="Cancel"
              handleButtonClick={handleClose}
              customStyle={{ height: "42px" }}
            />
          </Col>
          <Col lg="3" md="3" sm="3" xs="3">
            <CustomButton
              title="Add product"
              buttonType="validation"
              isValid={!!selectedVariant}
              handleButtonClick={handleAddProduct}
              customStyle={{ height: "42px", marginTop: 0 }}
            />
          </Col>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ProductSelectionModal;
